import {
  useEffect,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { StatusBadge, Surface, SurfaceHeader } from "../components/PortalPrimitives";
import {
  createMonitor,
  fetchCompanySearch,
  fetchMonitor,
  fetchMonitorEvaluations,
  fetchMonitors,
  updateMonitor,
} from "../lib/api";
import { useCan } from "../lib/session";
import type {
  ChipTone,
  CompanySearchResult,
  MonitorCreateRequest,
  MonitorEvaluation,
  MonitorKind,
  MonitorListRequest,
  MonitorOperationalStatus,
  MonitorSignalState,
  MonitorSummary,
  MonitorUpdateRequest,
} from "../lib/types";

type MonitorView = "review" | "all" | "archived";
type MonitorFilterState = NonNullable<MonitorListRequest["state"]>;

const KIND_LABELS: Record<MonitorKind, string> = {
  thesis_killer: "Thesis check",
  key_signal: "Key signal",
};

const CADENCE_TIER_LABELS = {
  portfolio: "Portfolio",
  active_bench: "Active bench",
  universe: "Universe",
} as const;

const FILTER_STATES: Array<{ value: MonitorFilterState; label: string }> = [
  { value: "needs_review", label: "Needs attention" },
  { value: "untested", label: "Untested" },
  { value: "ok", label: "No exception at last check" },
  { value: "watching", label: "Watching" },
  { value: "tripped", label: "Needs review" },
  { value: "paused", label: "Paused" },
  { value: "error", label: "Check issue" },
  { value: "pending", label: "Queued for check" },
  { value: "waiting", label: "Waiting for evidence" },
  { value: "evaluating", label: "Checking now" },
];

function parseView(value: string | null): MonitorView {
  return value === "all" || value === "archived" ? value : "review";
}

function parseFilterState(value: string | null): MonitorFilterState | undefined {
  return FILTER_STATES.some((option) => option.value === value)
    ? (value as MonitorFilterState)
    : undefined;
}

function formatWhen(value: string | null | undefined): string {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function When({ value }: { value: string | null | undefined }) {
  if (!value) return <>Never</>;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return <>Date unavailable</>;
  return <time dateTime={value}>{formatWhen(value)}</time>;
}

function formatNumber(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "Unavailable";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(value);
}

function hasHistoricalEvaluation(monitor: MonitorSummary): boolean {
  return monitor.has_evaluated === true
    || monitor.last_evaluation != null
    || Boolean(monitor.last_evaluated_at)
    || monitor.eval_count > 0;
}

function ruleNeedsEvaluation(monitor: MonitorSummary): boolean {
  return monitor.rule_needs_evaluation === true
    || monitor.current_rule_evaluated === false
    || monitor.last_evaluation?.applies_to_current_rule === false;
}

function signalFor(monitor: MonitorSummary): MonitorSignalState | null {
  if (ruleNeedsEvaluation(monitor)) return null;
  if (monitor.signal_state !== undefined) return monitor.signal_state;
  if (monitor.last_evaluation?.state) return monitor.last_evaluation.state;
  if (
    monitor.has_evaluated === false
    || (!monitor.last_evaluated_at && monitor.eval_count === 0)
  ) {
    return null;
  }
  return monitor.state === "paused" ? null : monitor.state;
}

function operationalFor(monitor: MonitorSummary): MonitorOperationalStatus {
  if (monitor.status === "archived") return "archived";
  if (monitor.state === "paused") return "paused";
  if (monitor.operational_status) return monitor.operational_status;
  if (monitor.in_progress) return "evaluating";
  if (ruleNeedsEvaluation(monitor) && hasHistoricalEvaluation(monitor)) return "pending";
  return signalFor(monitor) === null ? "untested" : "ready";
}

function signalPresentation(monitor: MonitorSummary): { label: string; tone: ChipTone } {
  if (ruleNeedsEvaluation(monitor) && hasHistoricalEvaluation(monitor)) {
    return { label: "Untested · updated rule", tone: "gray" };
  }
  const signal = signalFor(monitor);
  if (signal === "tripped") return { label: "Needs review", tone: "red" };
  if (signal === "watching") return { label: "Watching", tone: "amber" };
  if (signal === "ok") {
    return monitor.agent_kind === "key_signal"
      ? { label: "Rule passing", tone: "green" }
      : { label: "No breach found", tone: "green" };
  }
  return { label: "Untested", tone: "gray" };
}

function operationalPresentation(monitor: MonitorSummary): { label: string; tone: ChipTone } {
  const status = operationalFor(monitor);
  if (status === "archived") return { label: "Archived", tone: "gray" };
  if (status === "paused") return { label: "Paused", tone: "gray" };
  if (status === "evaluating" || status === "running") {
    return { label: "Checking now", tone: "blue" };
  }
  if (status === "pending") return { label: "Queued for check", tone: "blue" };
  if (monitor.last_attempt_outcome === "stale_fact") {
    return { label: "Fresh data needed", tone: "amber" };
  }
  if (status === "waiting") return { label: "Waiting for evidence", tone: "gray" };
  if (status === "error" || status === "failed") {
    return { label: "Check issue", tone: "red" };
  }
  if (status === "untested") return { label: "Not checked yet", tone: "gray" };
  return { label: "Active", tone: "blue" };
}

function hasSafeAttemptIssue(monitor: MonitorSummary): boolean {
  if (monitor.last_attempt_error?.trim()) return true;
  if (monitor.last_attempt_outcome === "configuration_error" || monitor.last_attempt_outcome === "failed") {
    return true;
  }
  if (monitor.last_attempt_outcome !== "running") return false;
  if (!monitor.evaluation_claimed_until) return true;
  const claimedUntil = Date.parse(monitor.evaluation_claimed_until);
  return !Number.isFinite(claimedUntil) || claimedUntil <= Date.now();
}

function appPath(path: string): string {
  return path.startsWith("/app/") ? path.slice(4) : path;
}

function companyPath(ticker: string): string {
  return `/companies/${encodeURIComponent(ticker)}`;
}

function conditionValue(condition: Record<string, unknown>, key: string): string {
  const value = condition[key];
  return typeof value === "string" ? value : "";
}

function thresholdValue(condition: Record<string, unknown>): string {
  const value = condition.threshold;
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
}

function conditionDescription(monitor: MonitorSummary): string {
  if (monitor.agent_kind === "thesis_killer") {
    return monitor.instructions?.trim() || "No thesis condition has been written.";
  }
  const factName = conditionValue(monitor.condition, "fact_name");
  const operator = conditionValue(monitor.condition, "operator");
  const threshold = monitor.condition.threshold;
  const periodType = conditionValue(monitor.condition, "period_type");
  if (!factName || !operator || typeof threshold !== "number") {
    return "The fact condition is incomplete.";
  }
  return `${factName} ${operator} ${formatNumber(threshold)}${periodType ? ` · ${periodType}` : ""}`;
}

function cadenceDescription(monitor: MonitorSummary): string {
  if (monitor.agent_kind === "key_signal") return "On matching fact refreshes";
  if (!monitor.cadence_tier) return "Coverage cadence not assigned";
  const tier = CADENCE_TIER_LABELS[monitor.cadence_tier];
  return monitor.cadence_floor_hours == null
    ? tier
    : `${tier} · minimum ${monitor.cadence_floor_hours}h between model checks`;
}

function StateMessage({
  title,
  detail,
  action,
  error = false,
}: {
  title: string;
  detail: string;
  action?: React.ReactNode;
  error?: boolean;
}) {
  return (
    <div className={`monitors-state${error ? " monitors-state--error" : ""}`} role={error ? "alert" : "status"}>
      <strong>{title}</strong>
      <span>{detail}</span>
      {action}
    </div>
  );
}

function trapDialogTab(event: KeyboardEvent<HTMLElement>, container: HTMLElement | null) {
  if (event.key !== "Tab" || !container) return;
  const focusable = Array.from(
    container.querySelectorAll<HTMLElement>(
      "button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])",
    ),
  ).filter((element) => !element.hasAttribute("hidden"));
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function EvaluationItem({
  evaluation,
  kind,
  priorRule,
}: {
  evaluation: MonitorEvaluation;
  kind: MonitorKind;
  priorRule: boolean;
}) {
  const signal = signalPresentation({
    agent_kind: kind,
    signal_state: evaluation.state,
    state: evaluation.state,
    has_evaluated: true,
    last_evaluation: null,
    last_evaluated_at: evaluation.evaluated_at,
    eval_count: 1,
  } as MonitorSummary);
  const evidence = Array.isArray(evaluation.evidence)
    ? evaluation.evidence.filter(
        (item) => item && typeof item === "object" && (item.type === "document_excerpt" || item.type === "fact_observation"),
      )
    : [];
  return (
    <article className="monitors-evaluation">
      <header>
        <div className="monitors-evaluation-title">
          {priorRule ? <StatusBadge tone="gray">Prior rule</StatusBadge> : null}
          <StatusBadge tone={signal.tone}>{signal.label}</StatusBadge>
          <strong><When value={evaluation.evaluated_at} /></strong>
        </div>
      </header>
      <p>{evaluation.summary?.trim() || "No assessment summary was stored."}</p>
      {evidence.length ? (
        <ul className="monitors-evidence" aria-label="Sources used for this assessment">
          {evidence.map((item, index) => {
            if (item.type === "document_excerpt") {
              const page = item.page == null
                ? "Page unavailable"
                : item.page_end && item.page_end !== item.page
                  ? `Pages ${item.page}–${item.page_end}`
                  : `Page ${item.page}`;
              return (
                <li key={`document-${index}`} className="monitors-evidence-item">
                  <span className="monitors-evidence-type">Document</span>
                  <div>
                    <Link to={appPath(item.deep_link)}>{item.title || "Untitled source"}</Link>
                    <span>{[item.source, item.date, page].filter(Boolean).join(" · ")}</span>
                  </div>
                </li>
              );
            }
            const observedDate = item.as_of_date
              ?? (["point_in_time", "event"].includes(item.period_type ?? "") ? item.period_end : null);
            const showObservedDate = Boolean(observedDate)
              && (["point_in_time", "event"].includes(item.period_type ?? "") || !item.period_end);
            return (
              <li key={`fact-${index}`} className="monitors-evidence-item">
                <span className="monitors-evidence-type">Fact</span>
                <div>
                  <strong>{item.fact_name}</strong>
                  <span>
                    {formatNumber(item.value)}
                    {showObservedDate
                      ? ` · observed ${observedDate}`
                      : item.period_end ? ` · period ended ${item.period_end}` : ""}
                    {item.period_type ? ` · ${item.period_type}` : ""}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="monitors-evidence-note">No source links were stored for this assessment.</div>
      )}
      {evaluation.duplicate_count ? (
        <div className="monitors-evidence-note">
          {evaluation.duplicate_count} duplicate {evaluation.duplicate_count === 1 ? "check" : "checks"} collapsed.
        </div>
      ) : null}
      {evaluation.evidence_unavailable_count ? (
        <div className="monitors-evidence-note">
          {evaluation.evidence_unavailable_count} stored source {evaluation.evidence_unavailable_count === 1 ? "link is" : "links are"} unavailable.
        </div>
      ) : null}
    </article>
  );
}

function EvaluationHistory({
  monitorId,
  kind,
  priorRule,
}: {
  monitorId: string;
  kind: MonitorKind;
  priorRule: boolean;
}) {
  const history = useInfiniteQuery({
    queryKey: ["monitor-evaluations", monitorId],
    queryFn: ({ pageParam }) => fetchMonitorEvaluations(monitorId, pageParam, 10),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    retry: false,
  });

  if (history.isLoading) {
    return <StateMessage title="Loading assessment history" detail="Retrieving the evidence-backed checks for this monitor…" />;
  }
  if (history.isError && !history.data) {
    return (
      <StateMessage
        title="Assessment history could not be loaded"
        detail="The monitor is still available. Retry the history request."
        error
        action={<button className="btn" type="button" onClick={() => void history.refetch()}>Retry history</button>}
      />
    );
  }
  const pages = history.data?.pages ?? [];
  const seen = new Set<string>();
  const evaluations = pages.flatMap((page) => page.evaluations).filter((evaluation) => {
    if (seen.has(evaluation.agent_evaluation_id)) return false;
    seen.add(evaluation.agent_evaluation_id);
    return true;
  });
  const isPriorRule = (evaluation: MonitorEvaluation) => evaluation.applies_to_current_rule === false
    || (evaluation.applies_to_current_rule === undefined && priorRule);
  const hasPriorRuleEvaluations = evaluations.some(isPriorRule);
  return (
    <div className="monitors-history" aria-busy={history.isFetching}>
      {hasPriorRuleEvaluations ? (
        <div className="monitors-prior-rule-note">
          Assessments marked Prior rule remain as audit history and are not the current signal.
        </div>
      ) : null}
      {history.isError ? (
        <div className="monitors-inline-warning" role="alert">
          Newer history could not be retrieved. Showing the checks already loaded.
          <button className="btn" type="button" onClick={() => void history.refetch()}>Retry</button>
        </div>
      ) : null}
      {evaluations.length ? evaluations.map((evaluation) => (
        <EvaluationItem
          key={evaluation.agent_evaluation_id}
          evaluation={evaluation}
          kind={kind}
          priorRule={isPriorRule(evaluation)}
        />
      )) : (
        <StateMessage
          title="No assessments yet"
          detail="This monitor is untested until its first matching document or fact refresh is checked."
        />
      )}
      {history.hasNextPage ? (
        <button
          className="btn monitors-load-more"
          type="button"
          disabled={history.isFetchingNextPage}
          onClick={() => void history.fetchNextPage()}
        >
          {history.isFetchingNextPage ? "Loading…" : "Load earlier assessments"}
        </button>
      ) : null}
      {history.isFetchNextPageError ? (
        <div className="monitors-inline-warning" role="alert">
          Earlier assessments could not be loaded.
          <button className="btn" type="button" onClick={() => void history.fetchNextPage()}>Retry earlier assessments</button>
        </div>
      ) : null}
    </div>
  );
}

function MonitorActions({
  monitor,
  writable,
  onEdit,
  onRestored,
  onArchived,
}: {
  monitor: MonitorSummary;
  writable: boolean;
  onEdit: (trigger: HTMLButtonElement) => void;
  onRestored: () => void;
  onArchived: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<"pause" | "archive" | null>(null);
  const pauseButton = useRef<HTMLButtonElement>(null);
  const archiveButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const confirmDialog = useRef<HTMLElement>(null);
  const status = operationalFor(monitor);
  const refresh = async (updated: MonitorSummary) => {
    queryClient.setQueryData(["monitor", monitor.agent_definition_id], { status: "ok", monitor: updated });
    await queryClient.invalidateQueries({ queryKey: ["monitors"] });
  };
  const update = useMutation({
    mutationFn: (request: MonitorUpdateRequest) => updateMonitor(monitor.agent_definition_id, request),
    onSuccess: async (response, request) => {
      await refresh(response.monitor);
      if (request.status === "archived") onArchived();
      if (request.status === "active") onRestored();
      setConfirming(null);
      if (request.paused === true) window.setTimeout(() => pauseButton.current?.focus());
    },
  });

  const restoreConfirmFocus = useCallback(() => {
    const target = confirming === "pause" ? pauseButton.current : archiveButton.current;
    window.setTimeout(() => target?.focus());
  }, [confirming]);

  useEffect(() => {
    if (!confirming) return;
    cancelButton.current?.focus();
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        if (update.isPending) return;
        event.preventDefault();
        setConfirming(null);
        restoreConfirmFocus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [confirming, restoreConfirmFocus, update.isPending]);

  if (!writable) return null;
  const archived = monitor.status === "archived" || status === "archived";
  return (
    <>
      <div className="monitors-detail-actions">
        {!archived ? (
          <button
            ref={pauseButton}
            className="btn"
            type="button"
            disabled={update.isPending}
            onClick={() => {
              if (status === "paused") update.mutate({ paused: false });
              else setConfirming("pause");
            }}
          >
            {update.isPending ? "Saving…" : status === "paused" ? "Resume checks" : "Pause future checks"}
          </button>
        ) : null}
        <button className="btn" type="button" disabled={update.isPending} onClick={(event) => onEdit(event.currentTarget)}>
          Edit rule
        </button>
        {archived ? (
          <button
            className="btn primary"
            type="button"
            disabled={update.isPending}
            onClick={() => update.mutate({ status: "active" })}
          >
            {update.isPending ? "Restoring…" : "Restore monitor"}
          </button>
        ) : (
          <button
            ref={archiveButton}
            className="btn monitors-danger-action"
            type="button"
            disabled={update.isPending}
            onClick={() => setConfirming("archive")}
          >
            Archive
          </button>
        )}
      </div>
      {update.isError ? (
        <div className="monitors-inline-warning" role="alert">The monitor could not be updated. Try again.</div>
      ) : null}
      {confirming ? (
        <div
          className="monitors-dialog-backdrop"
          onMouseDown={() => {
            if (!update.isPending) {
              setConfirming(null);
              restoreConfirmFocus();
            }
          }}
        >
          <section
            ref={confirmDialog}
            className="monitors-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="monitor-confirm-title"
            aria-describedby="monitor-confirm-detail"
            onMouseDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => trapDialogTab(event, confirmDialog.current)}
          >
            <h2 id="monitor-confirm-title">{confirming === "pause" ? "Pause future checks?" : "Archive this monitor?"}</h2>
            <p id="monitor-confirm-detail">
              {confirming === "pause"
                ? "Athena will not start new checks until you resume. A check already running may still finish, and the current signal remains visible."
                : "Athena will stop future checks. A check already running may still finish. Prior assessments remain available, and you can restore the monitor later."}
            </p>
            <div>
              <button
                ref={cancelButton}
                className="btn"
                type="button"
                disabled={update.isPending}
                onClick={() => {
                  setConfirming(null);
                  restoreConfirmFocus();
                }}
              >
                Cancel
              </button>
              <button
                className={confirming === "archive" ? "btn monitors-danger-button" : "btn primary"}
                type="button"
                disabled={update.isPending}
                onClick={() => {
                  if (confirming === "pause") update.mutate({ paused: true });
                  else update.mutate({ status: "archived" });
                }}
              >
                {update.isPending
                  ? confirming === "pause" ? "Pausing…" : "Archiving…"
                  : confirming === "pause" ? "Pause checks" : "Archive monitor"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}

function MonitorDetail({
  monitorId,
  writable,
  onClose,
  onEdit,
  onRestored,
  onArchived,
}: {
  monitorId: string;
  writable: boolean;
  onClose: () => void;
  onEdit: (monitor: MonitorSummary, trigger: HTMLButtonElement) => void;
  onRestored: () => void;
  onArchived: () => void;
}) {
  const detail = useQuery({
    queryKey: ["monitor", monitorId],
    queryFn: () => fetchMonitor(monitorId),
    retry: false,
  });
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeButton.current?.focus({ preventScroll: true });
  }, [monitorId]);

  return (
    <Surface id="monitor-detail-panel" className="monitors-detail" aria-labelledby="monitor-detail-title" aria-busy={detail.isFetching}>
      <div className="monitors-detail-close-row">
        <span className="monitors-eyebrow">Monitor detail</span>
        <button ref={closeButton} className="btn" type="button" onClick={onClose} aria-label="Close monitor details">Close</button>
      </div>
      {detail.isLoading ? (
        <StateMessage title="Loading monitor" detail="Retrieving its current rule and assessment history…" />
      ) : null}
      {detail.isError && !detail.data ? (
        <StateMessage
          title="Monitor could not be loaded"
          detail="It may have been removed or the link may be out of date."
          error
          action={<button className="btn" type="button" onClick={() => void detail.refetch()}>Retry detail</button>}
        />
      ) : null}
      {detail.data ? (() => {
        const monitor = detail.data.monitor;
        const signal = signalPresentation(monitor);
        const operational = operationalPresentation(monitor);
        const lastAttempt = monitor.last_attempt_at ?? monitor.last_attempted_at;
        const hasAttemptIssue = hasSafeAttemptIssue(monitor)
          || monitor.operational_status === "error"
          || monitor.operational_status === "failed";
        const hasMissingFact = monitor.last_attempt_outcome === "no_fact";
        const hasDataIssue = monitor.last_attempt_outcome === "stale_fact";
        const updatedRulePending = ruleNeedsEvaluation(monitor) && hasHistoricalEvaluation(monitor);
        const currentRuleAssessedAt = monitor.current_rule_evaluated_at !== undefined
          ? monitor.current_rule_evaluated_at
          : updatedRulePending
            ? null
            : monitor.last_evaluation?.evaluated_at ?? monitor.last_evaluated_at;
        return (
          <>
            {detail.isError ? (
              <div className="monitors-inline-warning" role="alert">Refresh failed. Showing the last loaded monitor detail.</div>
            ) : null}
            <header className="monitors-detail-head">
              <div>
                <Link className="monitors-detail-company" to={companyPath(monitor.ticker)}>
                  {monitor.ticker} · {monitor.company_name}
                </Link>
                <h2 id="monitor-detail-title">{monitor.name}</h2>
                <div className="monitors-badge-row">
                  <StatusBadge tone={signal.tone}>{signal.label}</StatusBadge>
                  <StatusBadge tone={operational.tone}>{operational.label}</StatusBadge>
                  <StatusBadge tone="blue">{KIND_LABELS[monitor.agent_kind]}</StatusBadge>
                </div>
                <Link className="monitors-research-action" to={`/operations?ticker=${encodeURIComponent(monitor.ticker)}`}>Open research actions</Link>
              </div>
              <MonitorActions
                monitor={monitor}
                writable={writable}
                onEdit={(trigger) => onEdit(monitor, trigger)}
                onRestored={() => {
                  onRestored();
                  window.setTimeout(() => closeButton.current?.focus({ preventScroll: true }));
                }}
                onArchived={onArchived}
              />
            </header>
            <div className={`monitors-callout${updatedRulePending ? " monitors-callout--pending-rule" : ""}`}>
              <strong>{updatedRulePending ? "Current-rule signal" : "Latest signal"}</strong>
              <p>{updatedRulePending
                ? "Awaiting evaluation for updated rule."
                : monitor.last_evaluation?.summary || monitor.state_summary || "No assessment has been completed yet."}</p>
              <span>{updatedRulePending
                ? <>The prior assessment from <When value={monitor.last_evaluation?.evaluated_at ?? monitor.last_evaluated_at} /> remains in history but does not apply to this rule.</>
                : "A signal prompts evidence review; it does not mean the thesis is broken or change a position."}</span>
            </div>
            {hasAttemptIssue ? (
              <div className="monitors-setup-issue" role="status">
                <strong>The latest check could not complete.</strong>
                <span>Review the rule setup, then retry after the next matching evidence event.</span>
              </div>
            ) : null}
            {hasMissingFact ? (
              <div className="monitors-setup-issue" role="status">
                <strong>A matching fact has not been stored yet.</strong>
                <span>Athena is waiting for that exact company fact instead of treating the rule as passing.</span>
              </div>
            ) : null}
            {hasDataIssue ? (
              <div className="monitors-setup-issue" role="status">
                <strong>A fresh matching fact is needed.</strong>
                <span>The stored fact is outside its supported freshness window, so Athena is waiting rather than issuing a fresh passing assessment.</span>
              </div>
            ) : null}
            <dl className="monitors-facts">
              <div><dt>Rule</dt><dd>{conditionDescription(monitor)}</dd></div>
              <div><dt>Trigger</dt><dd>{monitor.agent_kind === "thesis_killer" ? "When new documents arrive" : "When company facts refresh"}</dd></div>
              <div><dt>Owner</dt><dd>{monitor.owner || "Unassigned"}</dd></div>
              <div><dt>Cadence</dt><dd>{cadenceDescription(monitor)}</dd></div>
              <div>
                <dt>Next cadence eligibility</dt>
                <dd>{monitor.agent_kind === "key_signal"
                  ? "Not applicable"
                  : monitor.next_cadence_eligible_at
                    ? <When value={monitor.next_cadence_eligible_at} />
                    : "Eligible when matching evidence arrives"}</dd>
              </div>
              <div><dt>Current rule assessed</dt><dd><When value={currentRuleAssessedAt} /></dd></div>
              <div><dt>Last attempt</dt><dd><When value={lastAttempt} /></dd></div>
              <div><dt>Assessments</dt><dd>{monitor.eval_count}</dd></div>
              <div>
                <dt>Queue status</dt>
                <dd>{monitor.next_evaluation_due_at ? <>Queued since <When value={monitor.next_evaluation_due_at} /></> : operational.label}</dd>
              </div>
            </dl>
            <div className="monitors-bounded-note">
              Thesis checks assess a bounded set of newly available evidence and use model capacity. They are not continuous surveillance or a review of every historical source.
            </div>
            <section className="monitors-history-section" aria-labelledby="monitor-history-title">
              <div className="monitors-section-heading">
                <h3 id="monitor-history-title">Assessment history</h3>
                <span>Newest first</span>
              </div>
              <EvaluationHistory
                monitorId={monitor.agent_definition_id}
                kind={monitor.agent_kind}
                priorRule={updatedRulePending}
              />
            </section>
          </>
        );
      })() : null}
    </Surface>
  );
}

function useDebouncedValue(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [delay, value]);
  return debounced;
}

function MonitorEditor({
  monitor,
  returnFocus,
  onClose,
  onSaved,
}: {
  monitor: MonitorSummary | null;
  returnFocus: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onSaved: (monitor: MonitorSummary) => void;
}) {
  const editing = monitor !== null;
  const titleId = useId();
  const descriptionId = useId();
  const listboxId = useId();
  const firstInput = useRef<HTMLInputElement>(null);
  const editorDialog = useRef<HTMLElement>(null);
  const [name, setName] = useState(monitor?.name ?? "");
  const [kind, setKind] = useState<MonitorKind>(monitor?.agent_kind ?? "thesis_killer");
  const [companyQuery, setCompanyQuery] = useState(monitor ? `${monitor.ticker} · ${monitor.company_name}` : "");
  const [company, setCompany] = useState<CompanySearchResult | null>(monitor ? {
    issuer_id: null,
    security_id: null,
    listing_id: null,
    ticker: monitor.ticker,
    provider_display_ticker: monitor.ticker,
    company_name: monitor.company_name,
    exchange: null,
    mic: null,
    country: null,
    currency: null,
    security_type: "equity",
    is_primary: true,
    tier: null,
    tracked: true,
    document_count: 0,
    company_url: companyPath(monitor.ticker),
  } : null);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [activeCompanyIndex, setActiveCompanyIndex] = useState(-1);
  const [instructions, setInstructions] = useState(monitor?.instructions ?? "");
  const [factName, setFactName] = useState(monitor ? conditionValue(monitor.condition, "fact_name") : "");
  const [operator, setOperator] = useState(monitor ? conditionValue(monitor.condition, "operator") || ">" : ">");
  const [threshold, setThreshold] = useState(monitor ? thresholdValue(monitor.condition) : "");
  const [periodType, setPeriodType] = useState(monitor ? conditionValue(monitor.condition, "period_type") : "quarterly");
  const normalizedCompanyQuery = companyQuery.trim();
  const debouncedCompanyQuery = useDebouncedValue(normalizedCompanyQuery, 200);
  const companySearch = useQuery({
    queryKey: ["company-search", "monitor-editor", debouncedCompanyQuery],
    queryFn: ({ signal }) => fetchCompanySearch({ q: debouncedCompanyQuery, limit: 8 }, signal),
    enabled: !editing && debouncedCompanyQuery.length > 0 && company === null,
    staleTime: 60_000,
  });
  const results = company === null && companyOpen && normalizedCompanyQuery === debouncedCompanyQuery
    ? companySearch.data?.results ?? []
    : [];
  const selectableCompanyIndexes = results.flatMap((result, index) =>
    result.monitor_eligible === false ? [] : [index],
  );
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: () => {
      const triggerType = kind === "thesis_killer" ? "event_new_documents" : "facts_refresh";
      if (editing && monitor) {
        const request: MonitorUpdateRequest = {
          name: name.trim(),
          trigger_type: triggerType,
          ...(kind === "thesis_killer"
            ? { instructions: instructions.trim() }
            : {
                condition: {
                  fact_name: factName.trim(),
                  operator,
                  threshold: Number(threshold),
                  ...(periodType ? { period_type: periodType } : {}),
                },
              }),
        };
        return updateMonitor(monitor.agent_definition_id, request);
      }
      if (!company?.issuer_id || !company.ticker) {
        throw new Error("Select a company with an active Athena issuer listing.");
      }
      const request: MonitorCreateRequest = {
        name: name.trim(),
        agent_kind: kind,
        issuer_id: company.issuer_id,
        ticker: company.ticker.toUpperCase(),
        trigger_type: triggerType,
        ...(kind === "thesis_killer"
          ? { instructions: instructions.trim() }
          : {
              condition: {
                fact_name: factName.trim(),
                operator,
                threshold: Number(threshold),
                ...(periodType ? { period_type: periodType } : {}),
              },
            }),
      };
      return createMonitor(request);
    },
    onSuccess: async (response) => {
      queryClient.setQueryData(
        ["monitor", response.monitor.agent_definition_id],
        { status: "ok", monitor: response.monitor },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["monitors"] }),
        queryClient.invalidateQueries({
          queryKey: ["monitor-evaluations", response.monitor.agent_definition_id],
        }),
      ]);
      onSaved(response.monitor);
    },
  });

  useEffect(() => {
    firstInput.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        if (save.isPending) return;
        event.preventDefault();
        onClose();
        window.setTimeout(() => returnFocus.current?.focus());
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, returnFocus, save.isPending]);

  function dismiss() {
    if (save.isPending) return;
    onClose();
    window.setTimeout(() => returnFocus.current?.focus());
  }

  function selectCompany(result: CompanySearchResult) {
    setCompany(result);
    setCompanyQuery(`${result.ticker || result.provider_display_ticker || "—"} · ${result.company_name}`);
    setCompanyOpen(false);
    setActiveCompanyIndex(-1);
  }

  const ready = Boolean(
    name.trim()
    && (editing || (company?.issuer_id && company?.ticker && company.monitor_eligible !== false))
    && (kind === "thesis_killer"
      ? instructions.trim()
      : factName.trim() && threshold.trim() && Number.isFinite(Number(threshold))),
  );

  return (
    <div className="monitors-dialog-backdrop monitors-editor-backdrop" onMouseDown={dismiss}>
      <section
        ref={editorDialog}
        className="monitors-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => trapDialogTab(event, editorDialog.current)}
      >
        <header>
          <div>
            <span className="monitors-eyebrow">{editing ? "Edit monitor" : "New monitor"}</span>
            <h2 id={titleId}>{editing ? monitor.name : "Create a thesis monitor"}</h2>
            <p id={descriptionId}>Write one decision-useful condition for one company.</p>
          </div>
          <button
            className="btn"
            type="button"
            disabled={save.isPending}
            onClick={dismiss}
            aria-label="Close monitor editor"
          >
            Close
          </button>
        </header>
        <form
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (ready && !save.isPending) save.mutate();
          }}
        >
          <label className="monitors-field">
            <span>Monitor name</span>
            <input ref={firstInput} value={name} onChange={(event) => setName(event.target.value)} placeholder="Amazon DSP displaces TTD off-Amazon" />
          </label>
          <div className="monitors-editor-grid">
            <label className="monitors-field">
              <span>Type</span>
              <select value={kind} disabled={editing} onChange={(event) => setKind(event.target.value as MonitorKind)}>
                <option value="thesis_killer">Thesis check</option>
                <option value="key_signal">Key signal</option>
              </select>
            </label>
            <div className="monitors-field monitors-company-field">
              <label htmlFor={`${listboxId}-input`}>Company</label>
              {editing ? (
                <div className="monitors-locked-company">{monitor.ticker} · {monitor.company_name}</div>
              ) : (
                <>
                  <input
                    id={`${listboxId}-input`}
                    role="combobox"
                    aria-expanded={results.length > 0}
                    aria-controls={listboxId}
                    aria-autocomplete="list"
                    aria-activedescendant={activeCompanyIndex >= 0 && results[activeCompanyIndex]
                      ? `${listboxId}-option-${activeCompanyIndex}`
                      : undefined}
                    autoComplete="off"
                    value={companyQuery}
                    placeholder="Search ticker or company"
                    onFocus={() => setCompanyOpen(true)}
                    onChange={(event) => {
                      setCompanyQuery(event.target.value);
                      setCompany(null);
                      setCompanyOpen(true);
                      setActiveCompanyIndex(-1);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "ArrowDown") {
                        event.preventDefault();
                        if (selectableCompanyIndexes.length) {
                          setActiveCompanyIndex((current) => {
                            const position = selectableCompanyIndexes.indexOf(current);
                            return selectableCompanyIndexes[Math.min(position + 1, selectableCompanyIndexes.length - 1)];
                          });
                        }
                      } else if (event.key === "ArrowUp") {
                        event.preventDefault();
                        if (selectableCompanyIndexes.length) {
                          setActiveCompanyIndex((current) => {
                            const position = selectableCompanyIndexes.indexOf(current);
                            return selectableCompanyIndexes[
                              position <= 0 ? selectableCompanyIndexes.length - 1 : position - 1
                            ];
                          });
                        }
                      } else if (event.key === "Enter" && selectableCompanyIndexes.length) {
                        const selectedIndex = results[activeCompanyIndex]?.monitor_eligible === false
                          ? selectableCompanyIndexes[0]
                          : activeCompanyIndex >= 0
                            ? activeCompanyIndex
                            : selectableCompanyIndexes[0];
                        event.preventDefault();
                        selectCompany(results[selectedIndex]);
                      } else if (event.key === "Escape" && companyOpen) {
                        event.preventDefault();
                        event.stopPropagation();
                        setCompanyOpen(false);
                        setActiveCompanyIndex(-1);
                      }
                    }}
                  />
                  {companySearch.isFetching ? <span className="monitors-field-help" role="status">Searching companies…</span> : null}
                  {companySearch.isError ? <span className="monitors-field-error" role="alert">Company search is unavailable.</span> : null}
                  {results.length ? (
                    <div id={listboxId} className="monitors-company-results" role="listbox" aria-label="Company results">
                      {results.map((result, index) => (
                        <button
                          key={`${result.listing_id || result.issuer_id || result.ticker}-${index}`}
                          id={`${listboxId}-option-${index}`}
                          type="button"
                          role="option"
                          className={index === activeCompanyIndex ? "is-active" : undefined}
                          aria-selected={index === activeCompanyIndex}
                          aria-disabled={result.monitor_eligible === false}
                          disabled={result.monitor_eligible === false}
                          title={result.monitor_eligible === false
                            ? "This company is not active in Athena's monitor universe."
                            : undefined}
                          onMouseEnter={() => {
                            if (result.monitor_eligible !== false) setActiveCompanyIndex(index);
                          }}
                          onFocus={() => {
                            if (result.monitor_eligible !== false) setActiveCompanyIndex(index);
                          }}
                          onClick={() => selectCompany(result)}
                        >
                          <strong>{result.ticker || result.provider_display_ticker || "—"}</strong>
                          <span>
                            {result.company_name}
                            {result.monitor_eligible === false ? " · Not available for monitors" : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {company && !company.issuer_id ? (
                    <span className="monitors-field-error" role="alert">
                      This listing is not linked to an active Athena issuer and cannot be monitored yet.
                    </span>
                  ) : null}
                </>
              )}
            </div>
          </div>
          <div className="monitors-fixed-trigger">
            <span>Trigger</span>
            <strong>{kind === "thesis_killer" ? "New documents" : "Company facts refresh"}</strong>
            <small>{kind === "thesis_killer" ? "Checks only when relevant new evidence arrives." : "Checks the selected fact when company facts are refreshed."}</small>
          </div>
          {kind === "thesis_killer" ? (
            <label className="monitors-field">
              <span>Evidence condition</span>
              <textarea
                rows={6}
                value={instructions}
                onChange={(event) => setInstructions(event.target.value)}
                placeholder="Escalate when at least two independent sources show Amazon DSP directly displacing The Trade Desk in off-Amazon budgets; distinguish displacement from coexistence."
              />
              <small>State the evidence, independence, time window, and what would count as confirmation.</small>
            </label>
          ) : (
            <fieldset className="monitors-condition-fields">
              <legend>Single-fact condition</legend>
              <div className="monitors-condition-truth">
                This expression is the passing, healthy condition. Athena raises Needs review when it is false.
              </div>
              <label className="monitors-field monitors-fact-name">
                <span>Fact name</span>
                <input value={factName} onChange={(event) => setFactName(event.target.value)} placeholder="fmp.income_statement.revenueGrowth" />
              </label>
              <label className="monitors-field">
                <span>Operator</span>
                <select value={operator} onChange={(event) => setOperator(event.target.value)}>
                  <option value=">">&gt;</option>
                  <option value=">=">&gt;=</option>
                  <option value="<">&lt;</option>
                  <option value="<=">&lt;=</option>
                </select>
              </label>
              <label className="monitors-field">
                <span>Threshold</span>
                <input type="number" step="any" value={threshold} onChange={(event) => setThreshold(event.target.value)} />
              </label>
              <label className="monitors-field">
                <span>Period</span>
                <select value={periodType} onChange={(event) => setPeriodType(event.target.value)}>
                  <option value="quarterly">Quarterly</option>
                  <option value="semiannual">Semiannual</option>
                  <option value="annual">Annual</option>
                  <option value="ttm">Trailing twelve months</option>
                  <option value="point_in_time">Point in time</option>
                  <option value="event">Event</option>
                </select>
              </label>
            </fieldset>
          )}
          <div className="monitors-editor-caveat">
            {kind === "thesis_killer"
              ? "Each check uses a bounded set of new evidence and model capacity. A raised signal is a prompt for analyst review, not an automatic thesis decision."
              : "The check compares one stored company fact with this threshold. If the latest matching fact is missing or outside its supported freshness window, Athena waits instead of marking the rule as passing."}
            {editing ? " Saving queues the revised rule for its next matching evidence event; it does not replay all history." : ""}
          </div>
          {save.isError ? <div className="monitors-inline-warning" role="alert">The monitor could not be saved. Review the fields and try again.</div> : null}
          <footer>
            <button
              className="btn"
              type="button"
              disabled={save.isPending}
              onClick={dismiss}
            >
              Cancel
            </button>
            <button className="btn primary" type="submit" disabled={!ready || save.isPending}>
              {save.isPending ? "Saving…" : editing ? "Save rule" : "Create monitor"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

export default function Monitors() {
  const [params, setParams] = useSearchParams();
  const view = parseView(params.get("view"));
  const activeQuery = params.get("q") ?? "";
  const kind = params.get("kind") === "thesis_killer" || params.get("kind") === "key_signal"
    ? (params.get("kind") as MonitorKind)
    : undefined;
  const selectedState = parseFilterState(params.get("state"));
  const selectedMonitorId = params.get("monitor");
  const [draftQuery, setDraftQuery] = useState(activeQuery);
  const [editorMonitor, setEditorMonitor] = useState<MonitorSummary | null | undefined>(undefined);
  const returnFocus = useRef<HTMLButtonElement | null>(null);
  const detailReturnFocus = useRef<HTMLButtonElement | null>(null);
  const newButton = useRef<HTMLButtonElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const writable = useCan("workflows.write");

  useEffect(() => setDraftQuery(activeQuery), [activeQuery]);

  const request: MonitorListRequest = {
    q: activeQuery || undefined,
    status: view === "archived" ? "archived" : "active",
    state: view === "review" ? "needs_review" : selectedState,
    kind,
    limit: 25,
  };
  const list = useInfiniteQuery({
    queryKey: ["monitors", view, activeQuery, kind ?? "", selectedState ?? ""],
    queryFn: ({ pageParam, signal }) => fetchMonitors({ ...request, cursor: pageParam }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    retry: false,
  });

  const monitors = useMemo(() => {
    const rows = list.data?.pages.flatMap((page) => page.monitors) ?? [];
    const seen = new Set<string>();
    return rows.filter((row) => {
      if (seen.has(row.agent_definition_id)) return false;
      seen.add(row.agent_definition_id);
      return true;
    });
  }, [list.data]);
  const firstPage = list.data?.pages[0];
  const summary = firstPage?.summary;
  const total = firstPage?.total ?? monitors.length;

  function setParamValues(values: Record<string, string | null>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(values)) {
      if (value) next.set(key, value); else next.delete(key);
    }
    setParams(next);
  }

  function setView(nextView: MonitorView) {
    setParamValues({ view: nextView, state: null, monitor: null });
  }

  function selectMetric(metric: "review" | "untested" | "quiet" | "paused" | "issues" | "archived") {
    if (metric === "review") setParamValues({ view: "review", state: null, monitor: null });
    if (metric === "untested") setParamValues({ view: "all", state: "untested", monitor: null });
    if (metric === "quiet") setParamValues({ view: "all", state: "ok", monitor: null });
    if (metric === "paused") setParamValues({ view: "all", state: "paused", monitor: null });
    if (metric === "issues") setParamValues({ view: "all", state: "error", monitor: null });
    if (metric === "archived") setParamValues({ view: "archived", state: null, monitor: null });
  }

  const tabs: Array<{ view: MonitorView; label: string; count: number | undefined }> = [
    { view: "review", label: "Needs attention", count: summary?.needs_review },
    { view: "all", label: "All monitors", count: undefined },
    { view: "archived", label: "Archived", count: summary?.archived },
  ];

  function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex = index;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
    else if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = tabs.length - 1;
    else return;
    event.preventDefault();
    setView(tabs[nextIndex].view);
    window.setTimeout(() => tabRefs.current[nextIndex]?.focus());
  }

  return (
    <div className="monitors-page">
      <header className="pagehead monitors-pagehead">
        <div>
          <span className="monitors-eyebrow">Research exceptions</span>
          <h1>Thesis monitors</h1>
          <p className="sub">Review decision-useful thesis signals and exact company facts without turning a model check into a portfolio decision.</p>
        </div>
        <div className="monitors-page-actions">
          <button className="btn" type="button" onClick={() => void list.refetch()} disabled={list.isFetching}>
            {list.isFetching ? "Refreshing…" : "Refresh"}
          </button>
          {writable ? (
            <button
              ref={newButton}
              className="btn primary"
              type="button"
              onClick={() => {
                returnFocus.current = newButton.current;
                setEditorMonitor(null);
              }}
            >
              New monitor
            </button>
          ) : null}
        </div>
      </header>

      <Surface className="monitors-overview" aria-labelledby="monitors-overview-title">
        <SurfaceHeader
          title="Current posture"
          titleId="monitors-overview-title"
          detail="Counts reflect the current search and type filters; operational pauses remain separate from evidence signals."
        />
        <div className="monitors-metrics">
          <button type="button" onClick={() => selectMetric("review")} className="monitors-metric monitors-metric--attention">
            <span>Needs attention</span><strong>{summary?.needs_review ?? "—"}</strong>
          </button>
          <button type="button" onClick={() => selectMetric("untested")} className="monitors-metric">
            <span>Untested</span><strong>{summary?.untested ?? "—"}</strong>
          </button>
          <button
            type="button"
            onClick={() => selectMetric("quiet")}
            className="monitors-metric"
            aria-label={`No exception at last check: ${summary?.quiet ?? "count unavailable"}`}
          >
            <span title="No exception at last check">No exception</span><strong>{summary?.quiet ?? "—"}</strong>
          </button>
          <button type="button" onClick={() => selectMetric("paused")} className="monitors-metric">
            <span>Paused</span><strong>{summary?.paused ?? "—"}</strong>
          </button>
          {summary?.setup_issues !== undefined ? (
            <button type="button" onClick={() => selectMetric("issues")} className="monitors-metric monitors-metric--issue">
              <span>Check issues</span><strong>{summary.setup_issues}</strong>
            </button>
          ) : null}
          <button type="button" onClick={() => selectMetric("archived")} className="monitors-metric">
            <span>Archived</span><strong>{summary?.archived ?? "—"}</strong>
          </button>
        </div>
      </Surface>

      <div className="monitors-tabs" role="tablist" aria-label="Monitor views">
        {tabs.map((tab, index) => (
          <button
            key={tab.view}
            ref={(node) => { tabRefs.current[index] = node; }}
            id={`monitors-tab-${tab.view}`}
            type="button"
            role="tab"
            aria-selected={view === tab.view}
            aria-controls="monitors-panel"
            tabIndex={view === tab.view ? 0 : -1}
            onClick={() => setView(tab.view)}
            onKeyDown={(event) => onTabKeyDown(event, index)}
          >
            {tab.label}{tab.count !== undefined ? <span>{tab.count}</span> : null}
          </button>
        ))}
      </div>

      <div className={`monitors-workspace${selectedMonitorId ? " monitors-workspace--detail" : ""}`}>
        <Surface
          id="monitors-panel"
          className="monitors-list-surface"
          role="tabpanel"
          aria-labelledby={`monitors-tab-${view}`}
          aria-busy={list.isFetching}
        >
          <form
            className="monitors-filters"
            role="search"
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              setParamValues({ q: draftQuery.trim() || null, monitor: null });
            }}
          >
            <label className="monitors-search-field">
              <span className="sr-only">Search monitors</span>
              <input type="search" value={draftQuery} onChange={(event) => setDraftQuery(event.target.value)} placeholder="Search company, ticker, or rule" />
            </label>
            <button className="btn" type="submit">Search</button>
            <label>
              <span className="sr-only">Monitor type</span>
              <select value={kind ?? ""} onChange={(event) => setParamValues({ kind: event.target.value || null, monitor: null })}>
                <option value="">All types</option>
                <option value="thesis_killer">Thesis checks</option>
                <option value="key_signal">Key signals</option>
              </select>
            </label>
            {view !== "review" ? (
              <label>
                <span className="sr-only">Signal or operational state</span>
                <select value={selectedState ?? ""} onChange={(event) => setParamValues({ state: event.target.value || null, monitor: null })}>
                  <option value="">All states</option>
                  {FILTER_STATES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </label>
            ) : null}
            {(activeQuery || kind || selectedState) ? (
              <button className="btn" type="button" onClick={() => setParamValues({ q: null, kind: null, state: null, monitor: null })}>Clear filters</button>
            ) : null}
          </form>
          <div className="monitors-results-summary" aria-live="polite">
            Showing {monitors.length} of {total} {total === 1 ? "monitor" : "monitors"}
          </div>
          {list.isError && list.data ? (
            <div className="monitors-inline-warning" role="alert">
              Refresh failed. Showing the monitors already loaded.
              <button className="btn" type="button" onClick={() => void list.refetch()}>Retry</button>
            </div>
          ) : null}
          {list.isLoading ? (
            <StateMessage title="Loading thesis monitors" detail="Applying the selected server filters…" />
          ) : null}
          {list.isError && !list.data ? (
            <StateMessage
              title="Monitors could not be loaded"
              detail="Retry the request. No monitor settings have been changed."
              error
              action={<button className="btn" type="button" onClick={() => void list.refetch()}>Retry monitors</button>}
            />
          ) : null}
          {!list.isLoading && !list.isError && monitors.length === 0 ? (
            <StateMessage
              title={view === "review" ? "Nothing needs attention" : view === "archived" ? "No archived monitors" : "No monitors match"}
              detail={view === "review" ? "No active monitor currently has a raised signal or check issue." : activeQuery || kind || selectedState ? "Try clearing one or more filters." : "Create a focused rule when there is a decision-useful thesis condition to track."}
              action={activeQuery || kind || selectedState ? <button className="btn" type="button" onClick={() => setParamValues({ q: null, kind: null, state: null })}>Clear filters</button> : undefined}
            />
          ) : null}
          {monitors.length ? (
            <div className="monitors-table-wrap">
              <table className="monitors-table">
                <caption className="sr-only">Thesis monitors matching the current view and server filters</caption>
                <thead><tr><th scope="col">Monitor</th><th scope="col">Signal</th><th scope="col">Operations</th><th scope="col">Last assessed</th><th scope="col"><span className="sr-only">Action</span></th></tr></thead>
                <tbody>
                  {monitors.map((monitor) => {
                    const signal = signalPresentation(monitor);
                    const operational = operationalPresentation(monitor);
                    const selected = selectedMonitorId === monitor.agent_definition_id;
                    return (
                      <tr key={monitor.agent_definition_id} className={selected ? "is-selected" : undefined}>
                        <td data-label="Monitor">
                          <strong>{monitor.name}</strong>
                          <span><Link to={companyPath(monitor.ticker)}>{monitor.ticker}</Link> · {monitor.company_name} · {KIND_LABELS[monitor.agent_kind]}</span>
                        </td>
                        <td data-label="Signal"><StatusBadge tone={signal.tone}>{signal.label}</StatusBadge></td>
                        <td data-label="Operations"><StatusBadge tone={operational.tone}>{operational.label}</StatusBadge></td>
                        <td data-label="Last assessed"><span className="monitors-table-date"><When value={monitor.last_evaluation?.evaluated_at ?? monitor.last_evaluated_at} /></span></td>
                        <td data-label="Action">
                          <button
                            className="btn"
                            type="button"
                            aria-label={`View details for ${monitor.name}`}
                            aria-expanded={selected}
                            aria-controls="monitor-detail-panel"
                            onClick={(event) => {
                              detailReturnFocus.current = event.currentTarget;
                              setParamValues({ monitor: monitor.agent_definition_id });
                            }}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
          {list.hasNextPage ? (
            <button className="btn monitors-load-more" type="button" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              {list.isFetchingNextPage ? "Loading…" : "Load more monitors"}
            </button>
          ) : null}
          {list.isFetchNextPageError ? (
            <div className="monitors-inline-warning" role="alert">
              More monitors could not be loaded.
              <button className="btn" type="button" onClick={() => void list.fetchNextPage()}>Retry more monitors</button>
            </div>
          ) : null}
        </Surface>

        {selectedMonitorId ? (
          <MonitorDetail
            monitorId={selectedMonitorId}
            writable={writable}
            onClose={() => {
              const origin = detailReturnFocus.current;
              detailReturnFocus.current = null;
              setParamValues({ monitor: null });
              window.setTimeout(() => {
                if (origin?.isConnected) {
                  origin.focus();
                  return;
                }
                const activeTabIndex = tabs.findIndex((tab) => tab.view === view);
                (tabRefs.current[activeTabIndex] ?? newButton.current)?.focus();
              });
            }}
            onEdit={(monitor, trigger) => {
              returnFocus.current = trigger;
              setEditorMonitor(monitor);
            }}
            onArchived={() => {
              setParamValues({ monitor: null });
              const activeTabIndex = tabs.findIndex((tab) => tab.view === view);
              window.setTimeout(() => tabRefs.current[activeTabIndex]?.focus());
            }}
            onRestored={() => setParamValues({ view: "all", monitor: selectedMonitorId })}
          />
        ) : null}
      </div>

      {editorMonitor !== undefined ? (
        <MonitorEditor
          monitor={editorMonitor}
          returnFocus={returnFocus}
          onClose={() => setEditorMonitor(undefined)}
          onSaved={(monitor) => {
            const wasCreating = editorMonitor === null;
            setEditorMonitor(undefined);
            setParamValues({ view: "all", state: null, monitor: monitor.agent_definition_id });
            if (!wasCreating) window.setTimeout(() => returnFocus.current?.focus());
          }}
        />
      ) : null}
    </div>
  );
}
